import { array, number, object } from 'yup';
import {
  CustomDistributionScheduleItemDTO,
  DeferredCompensationContributionUnit,
  DeferredCompensationDistributionMethodDTO,
  DeferredCompensationDTO,
  DeferredCompensationOptionsDTO,
} from '../../tables/DeferredCompensation/DeferredCompensation.meta';
import { OptionType } from '../../../../CommonComponents/OPL/Select/BaseSelect/types';
import { OwnerModelDTO, OwnerModelFormDTO } from '../../OtherAssets.meta';

export const PRINCIPAL_ID = '00000000-0000-0000-0000-000000000000';

interface FormContext {
  options: DeferredCompensationOptionsDTO;
  principalDob: string | null;
  spouseDob: string | null;
}

export interface SaveDeferredCompensationDTO
  extends Omit<DeferredCompensationDTO, 'Id' | 'Name' | 'YearlyContribution' | 'OtherAssetType'> {
  Id: string | null;
  Name: string | null;
  YearlyContribution: number | null;
  ClientId: string;
  IsLiquid: boolean;
  IncludeInGrossEstate: boolean;
  OwnerModel: OwnerModelDTO[];
}

export interface DeferredCompensationFormValues {
  id: string | null;
  name: string | null;
  earner: string;
  value: number | null;
  yearlyContribution: number | null;
  contributionUnit: DeferredCompensationContributionUnit;
  contributionStartAge: number;
  contributionEndYear: number | null;
  distributionStartAge: number | null;
  distributionPeriod: number;
  accumulationGrowthRate: number;
  distributionGrowthRate: number;
  customBucketIndex: number | null;
  isLiquid: boolean;
  includeInGrossEstate: boolean;
  orionManagementStyleId: number | null;
  distributionMethod: DeferredCompensationDistributionMethodDTO;
  customDistributionSchedule: CustomDistributionScheduleItemDTO[];
  excludeFromNetWorth: boolean;
  ownerModel: OwnerModelFormDTO[];
}

interface DistributionStartAgeValidationParams {
  distributionStartAge: number | null | undefined;
  contributionEndYear: number | null;
  ownerModel: OwnerModelFormDTO[];
  options: DeferredCompensationOptionsDTO;
  principalDob: string | null;
  spouseDob: string | null;
}

interface BirthYearParams {
  isEarnerPrincipal: boolean;
  principalDob: string | null;
  spouseDob: string | null;
}

interface GetContributionEndAgeParams {
  contributionEndYear: number | null;
  earnerBirthYear: number | null;
  earnerRetirementAge: number;
  earnerAge: number;
  currentYear?: number;
}

export const getEarnerBirthYear = ({
  isEarnerPrincipal,
  principalDob,
  spouseDob,
}: BirthYearParams): number | null => {
  const dob = isEarnerPrincipal ? principalDob : spouseDob;
  return dob ? new Date(dob).getFullYear() : null;
};

export const getContributionEndAge = ({
  contributionEndYear,
  earnerBirthYear,
  earnerRetirementAge,
  earnerAge,
  currentYear = new Date().getFullYear(),
}: GetContributionEndAgeParams): number => {
  if (contributionEndYear === null) {
    return earnerRetirementAge;
  }

  if (earnerBirthYear !== null) {
    return contributionEndYear - earnerBirthYear;
  }

  // DOB missing: still convert the selected end year (do not substitute retirement age)
  return contributionEndYear - currentYear + earnerAge;
};

export const isDistributionStartAgeValid = ({
  distributionStartAge,
  contributionEndYear,
  ownerModel,
  options,
  principalDob,
  spouseDob,
}: DistributionStartAgeValidationParams): boolean => {
  const isAtRetirement = distributionStartAge == null;
  // For isEarnerPrincipal to be true, Check if the owner is not spouse. It can be either Principal or any dependent
  const isEarnerPrincipal = !(options.earner.People[1]?.Name === ownerModel[0].Name);
  const earnerRetirementAge = isEarnerPrincipal
    ? options.principalRetirementAge
    : options.spouseRetirementAge;
  const earnerAge = isEarnerPrincipal ? options.principalAge : options.spouseAge;
  const isContributionEndYearRetirement = contributionEndYear === null;
  const earnerBirthYear = getEarnerBirthYear({ isEarnerPrincipal, principalDob, spouseDob });
  const contributionEndAge = getContributionEndAge({
    contributionEndYear,
    earnerBirthYear,
    earnerRetirementAge,
    earnerAge,
  });

  if (isContributionEndYearRetirement && isAtRetirement) {
    return true;
  }

  if (isAtRetirement) {
    return earnerRetirementAge > contributionEndAge;
  }

  if (distributionStartAge != null) {
    return distributionStartAge > contributionEndAge;
  }

  return true;
};

export const DEFERRED_COMPENSATION_CONTRIBUTION_UNIT_OPTIONS: OptionType<
  DeferredCompensationContributionUnit
>[] = [
  { label: '$', value: DeferredCompensationContributionUnit.Dollar },
  { label: '%', value: DeferredCompensationContributionUnit.Percentage },
];

export const DEFERRED_COMPENSATION_FORM_SCHEMA = object({
  value: number().typeError('Enter the current value of this asset'),
  yearlyContribution: number()
    .transform((value, originalValue) => (originalValue === null ? null : value))
    .nullable()
    .when('contributionUnit', {
      is: DeferredCompensationContributionUnit.Percentage,
      then: schema => schema.max(100, 'The annual contribution must be less or equal to 100%'),
    }),
  accumulationGrowthRate: number().typeError('Enter the accumulation growth rate'),
  distributionGrowthRate: number().typeError('Enter the distribution growth rate'),
  distributionStartAge: number()
    .nullable()
    .test(
      'lessThanContributionEndYear',
      'Distribution must occur after contribution end year',
      // eslint-disable-next-line func-names
      function(value) {
        const { contributionEndYear, ownerModel } = this.parent;
        const { options, principalDob, spouseDob } = this.options.context as FormContext;

        return isDistributionStartAgeValid({
          distributionStartAge: value,
          contributionEndYear,
          ownerModel,
          options,
          principalDob,
          spouseDob,
        });
      },
    ),
  customDistributionSchedule: array()
    .of(
      object({
        Unit: number().required(),
        Value: number()
          .required()
          .test(
            'maxPercentage',
            'Percentage must be less than or equal to 100%',
            // eslint-disable-next-line func-names
            function(value) {
              const { Unit } = this.parent;
              if (Unit === DeferredCompensationContributionUnit.Percentage && value !== undefined) {
                return value <= 100;
              }
              return true;
            },
          )
          .test(
            'minValue',
            'Value must be greater than or equal to 0',
            // eslint-disable-next-line func-names
            function(value) {
              if (value !== undefined) {
                return value >= 0;
              }
              return true;
            },
          ),
      }),
    )
    .default([]),
});
